import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TimerSettings } from '../types';
import PomodoroSettingsService from '../services/PomodoroSettingsService';

// Query keys
export const POMODORO_SETTINGS_QUERY_KEYS = {
  settings: ['pomodoroSettings'] as const,
} as const;

// Get pomodoro settings
const getPomodoroSettings = async (): Promise<TimerSettings> => {
  return PomodoroSettingsService.getPomodoroSettings();
};

// Update pomodoro settings
const updatePomodoroSettings = async (updates: Partial<TimerSettings>): Promise<TimerSettings> => {
  return PomodoroSettingsService.updatePomodoroSettings(updates);
};

// Reset pomodoro settings to defaults
const resetPomodoroSettings = async (): Promise<TimerSettings> => {
  return PomodoroSettingsService.resetPomodoroSettings();
};

// React Query hooks
export const usePomodoroSettings = () => {
  return useQuery({
    queryKey: POMODORO_SETTINGS_QUERY_KEYS.settings,
    queryFn: getPomodoroSettings,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
  });
};

export const useUpdatePomodoroSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updatePomodoroSettings,
    onSuccess: (updatedSettings) => {
      // Update the cache with new settings
      queryClient.setQueryData(POMODORO_SETTINGS_QUERY_KEYS.settings, updatedSettings);
    },
    onError: (error) => {
      console.error('Failed to update pomodoro settings:', error);
    },
  });
};

export const useResetPomodoroSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: resetPomodoroSettings,
    onSuccess: (defaultSettings) => {
      // Update the cache with default settings
      queryClient.setQueryData(POMODORO_SETTINGS_QUERY_KEYS.settings, defaultSettings);
    },
    onError: (error) => {
      console.error('Failed to reset pomodoro settings:', error);
    },
  });
}; 